import DOMPurify from 'dompurify'
import { ChangeEvent, KeyboardEvent, memo, SyntheticEvent, useCallback, useDeferredValue, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Link as RouterLink } from 'react-router-dom'

import { ArrowForward, Close, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { BlueprintFamilyExampleImage, resolveExampleImageGameName } from '../../../../api/blueprintFamilies/exampleImages'
import {
  BlueprintFamilyApiItem,
  BlueprintFamilyGlossaryItem,
  useGetBlueprintFamiliesQuery,
  useGetBlueprintFamilyGlossaryQuery,
  useGetTagGroupsListQuery,
} from '../../../../api/core'
import { LiveEventSourceCaption } from '../../../../components/LiveEventSourceCaption/LiveEventSourceCaption'
import MultiSlider from '../../../../components/MultiSlider/MultiSlider'
import { normalizeForSearch } from '../../../../helpers/string'
import languageService from '../../../../services/LanguageService'
import { LockedGameSummary } from '../../../../types/LockedFeature'
import { TranslationMap } from '../../../../types/TranslationMap'
import { useInternalAccessCheck, useLiveEventsTrackerAccessCheck } from '../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { useExampleImageGame } from '../../../live-events/hooks/useExampleImageGame'
import { useLockedGameDialog } from '../../../live-events/hooks/useLockedGameDialog'
import { BlueprintScreenshotLightbox } from '../../../market-analytics/components/BlueprintScreenshotLightbox'
import { EventFilterCard } from '../../../market-analytics/components/EventFilterCard'
import {
  buildBlueprintFamilyPath,
  buildEventCategoryOptions,
  buildEventTypeOptions,
  buildEventTypeCategoryLookup,
  EventTypeCategoryLookup,
  familyGlossaryEventTypeMemberships,
} from '../../../market-analytics/components/helpers/catalogueUtils'
import { categoryTintSx } from '../../../market-analytics/components/helpers/filterChipStyles'
import { DEFAULT_TAG_GROUP_COLOR, TagGroupInfo } from '../../../market-analytics/components/helpers/tagGroups'
import { useMarketCatalogueState } from '../../../market-analytics/hooks/useMarketCatalogueState'
import { MarketLevelBlueprintListItem } from '../../../market-analytics/types/MarketLevelBlueprintListItem'
import { useIncrementalRender } from '../../hooks/useIncrementalRender'

// Cards sit in a responsive grid, so a portrait phone screenshot rendered at the
// card's full width makes one card several times taller than its neighbours. The
// image is boxed to a fixed height and letterboxed against the page background
// instead, the same treatment the Live Event Analytics screenshot card uses.
const GALLERY_IMAGE_HEIGHT = 260

// `gameAllowed` is `false` for a game outside the user's plan — for a free user, every game
// that isn't one of the free Live Events games. Absent reads as allowed.
const isLockedImage = (image: BlueprintFamilyExampleImage) => image.gameAllowed === false

type LocalizedGlossaryEntry = {
  id: string
  fullName: string
  category: string
  name: string
  descriptionHtml: string
  images: BlueprintFamilyExampleImage[]
  // Search keys, normalized once when the entry is localized so typing never re-normalizes
  // every family name on each keystroke.
  searchFullName: string
  searchName: string
}

type CategoryGroup = {
  // Event-category (tag group) id, or `UNCATEGORIZED_GROUP_ID` for the trailing
  // bucket. Only used as a React key + a test hook; never shown.
  id: string
  name: string
  items: LocalizedGlossaryEntry[]
}

// A group as rendered: `items` is the slice of cards on screen so far, `totalCount` the group's
// real size (what the heading reports).
type VisibleCategoryGroup = CategoryGroup & { totalCount: number }

// Also the `ecat` value of the "Uncategorized" pill. Real event category ids are 24-char hex, so
// this can't collide with one. The catalogue page validates `ecat` against the event taxonomy and
// drops it from a pasted link; this tab doesn't pass a taxonomy, so it survives the round trip.
const UNCATEGORIZED_GROUP_ID = 'uncategorized'

const NO_MEMBERSHIPS: ReturnType<typeof familyGlossaryEventTypeMemberships> = []
const NO_EVENT_TYPES: CardEventType[] = []

const byLocalizedName = (a: LocalizedGlossaryEntry, b: LocalizedGlossaryEntry) => a.name.localeCompare(b.name)

// Group ids are tag-group ids in the normal path but localized category names in the fallback
// grouping, so they are sanitized before going into the DOM as an `id`.
const groupHeadingId = (groupId: string) => `blueprint-family-group-${groupId.replace(/[^\w-]/g, '-')}`

// FE-side language fallback. The BE normalizes missing translations to empty strings (rather
// than omitting the key), so a truthy check covers both "missing" and "empty" the same way.
const localize = (value: TranslationMap | undefined, language: string): string => {
  if (!value) return ''
  return value[language as keyof TranslationMap] || value.en || ''
}

// The BE doesn't ship a `category` field on the glossary; it encodes categories in the localized
// name as `"<Category>: <Name>"` (e.g. `"PvP: Ranked Season"`, `"Monetization - Bundle/Offer: Limited-time Shop"`).
// Split on the FIRST colon only — categories like `"Monetization - Bundle/Offer"` contain hyphens
// and slashes but never colons. Falls back to an empty category + the full name if no colon is found.
//
// Cards are grouped by the real event taxonomy now, so the split's main job is the card TITLE:
// with the category named in the heading above, a card reading "PvP: Ranked Season" would repeat
// it. The parsed category is only still used by `groupByNamePrefix`, the fallback grouping.
const splitNameIntoCategoryAndTitle = (fullName: string): { category: string; name: string } => {
  const colonIndex = fullName.indexOf(':')
  if (colonIndex === -1) {
    return { category: '', name: fullName }
  }
  return {
    category: fullName.slice(0, colonIndex).trim(),
    name: fullName.slice(colonIndex + 1).trim(),
  }
}

const toLocalizedEntry = (entry: BlueprintFamilyGlossaryItem, language: string): LocalizedGlossaryEntry => {
  const fullName = localize(entry.names, language)
  const { category, name } = splitNameIntoCategoryAndTitle(fullName)
  const rawDescription = localize(entry.descriptions, language)
  return {
    id: entry.id,
    fullName,
    category,
    name,
    descriptionHtml: rawDescription ? DOMPurify.sanitize(rawDescription) : '',
    images: entry.images ?? [],
    searchFullName: normalizeForSearch(fullName),
    searchName: normalizeForSearch(name),
  }
}

// Localizing sanitizes every description with DOMPurify, which is the expensive part of building the
// tab's data. RTK Query hands back the same glossary item objects for as long as the response is
// cached, so keying on the item (weakly, so a dropped response frees its entries) lets a revisit to
// the tab reuse the work instead of rebuilding every entry from scratch.
const localizedEntryCache = new WeakMap<BlueprintFamilyGlossaryItem, Map<string, LocalizedGlossaryEntry>>()

const getLocalizedEntry = (entry: BlueprintFamilyGlossaryItem, language: string): LocalizedGlossaryEntry => {
  let byLanguage = localizedEntryCache.get(entry)
  if (!byLanguage) {
    byLanguage = new Map()
    localizedEntryCache.set(entry, byLanguage)
  }
  let localized = byLanguage.get(language)
  if (!localized) {
    localized = toLocalizedEntry(entry, language)
    byLanguage.set(language, localized)
  }
  return localized
}

// The text search reaches blueprint family NAMES only — the event category / event type pills
// do their own narrowing on top of its result. `fullName` is what the BE ships ("PvP: Ranked
// Season"), `name` the title the card shows ("Ranked Season"), so the reader can type either
// what they see or what they remember. The title is a slice of the full name as the split works
// today; matching both keeps the search honest if that ever stops being true.
const matchesNameQuery = (entry: LocalizedGlossaryEntry, normalizedQuery: string) =>
  entry.searchFullName.includes(normalizedQuery) || entry.searchName.includes(normalizedQuery)

// Fallback grouping, used only when the catalogue's family list or the event tag groups are
// unavailable (see `catalogueDataReady`): buckets entries by the category encoded in the BE's
// name, alphabetically, with the "no colon in the name" bucket last. This was the tab's only
// grouping before the real event taxonomy was joined in.
const groupByNamePrefix = (entries: LocalizedGlossaryEntry[], uncategorizedLabel: string): CategoryGroup[] => {
  const buckets = new Map<string, LocalizedGlossaryEntry[]>()
  for (const entry of entries) {
    const key = entry.category || uncategorizedLabel
    const bucket = buckets.get(key)
    if (bucket) bucket.push(entry)
    else buckets.set(key, [entry])
  }

  return Array.from(buckets.entries())
    .map(([name, items]) => ({ id: name, name, items: items.sort(byLocalizedName) }))
    .sort((a, b) => {
      if (a.name === uncategorizedLabel) return 1
      if (b.name === uncategorizedLabel) return -1
      return a.name.localeCompare(b.name)
    })
}

// Minimal `MarketLevelBlueprintListItem` for the chip-count builders, which only read
// `tagGroupId` / `tagId`. The remaining fields are filled from the family so the shape stays
// honest rather than zero-stuffed.
const toPlacementItem = (family: BlueprintFamilyApiItem, categoryId: string, typeId: string): MarketLevelBlueprintListItem => ({
  id: family.id,
  name: family.names,
  tagGroupId: categoryId,
  tagId: typeId,
  numberOfGames: family.gamesCount ?? 0,
  adoptingGamesInSegment: family.distinctGamesCount ?? null,
  numberOfLiveEventObjects: family.liveEventsCount ?? 0,
})

// One placement item per (family, dimension value), deduped so a family counts once per value.
const buildPlacementItems = (families: BlueprintFamilyApiItem[], dimension: 'categoryId' | 'typeId', categoryIdByTypeId: EventTypeCategoryLookup) => {
  const seen = new Set<string>()
  const items: MarketLevelBlueprintListItem[] = []
  for (const family of families) {
    for (const membership of familyGlossaryEventTypeMemberships(family, categoryIdByTypeId)) {
      const key = `${family.id}|${membership[dimension]}`
      if (seen.has(key)) continue
      seen.add(key)
      items.push(toPlacementItem(family, membership.categoryId, membership.typeId))
    }
  }
  return items
}

// The search recounts the pills, so a pill with no match left is dropped. An APPLIED pill must
// outlive that so the reader can still see it is filtering and clear it. The row is walked in the
// unsearched options' own order, so a pill never jumps position while the reader types: it stays
// if the search (and `isOffered`, the row's own scoping) still reaches it or it is applied, and its
// count comes from the searched options, zero when the search no longer reaches it.
const retainAppliedOptions = <T extends { id: string; blueprintCount: number }>(
  all: T[],
  searched: T[],
  appliedIds: string[],
  isOffered: (option: T) => boolean = () => true
): T[] => {
  const searchedCounts = new Map(searched.map((option) => [option.id, option.blueprintCount]))
  return all
    .filter((option) => (isOffered(option) && searchedCounts.has(option.id)) || appliedIds.includes(option.id))
    .map((option) => ({ ...option, blueprintCount: searchedCounts.get(option.id) ?? 0 }))
}

type GalleryImageProps = {
  image: BlueprintFamilyExampleImage
  index: number
  blueprintName: string
  // Whether the shot is locked for this reader (see `BlueprintImageGallery`): it then opens the unlock dialog, not the lightbox.
  locked: boolean
  // `lockedGame` is only passed for a locked image: the source game, with the icon and publisher the unlock dialog shows.
  onOpen: (event: SyntheticEvent, index: number, lockedGame?: LockedGameSummary) => void
  onLockedClick: (event: SyntheticEvent, game?: LockedGameSummary) => void
}

// The image opens the lightbox, the caption under it opens the source event, so the
// caption stays outside the image's click target rather than nested inside it. A shot from
// a game outside the user's plan is shown as-is but opens the unlock dialog instead.
const GalleryImage: React.FC<GalleryImageProps> = ({ image, index, blueprintName, locked, onOpen, onLockedClick }) => {
  const { t } = useTranslation()
  // Only a locked image needs the game here (for the unlock dialog); the caption below already
  // fetches it for every image, and RTK Query dedupes the two by args.
  const { lockedGame } = useExampleImageGame(image, locked)

  const openImage = (event: SyntheticEvent) => onOpen(event, index, locked ? lockedGame : undefined)
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openImage(event)
    }
  }

  const ariaLabel = locked ? t('live-events:locked_game_tooltip') : t('data-glossary:market_level_blueprints_view_image_aria')

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={openImage}
        onKeyDown={handleKeyDown}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          height: GALLERY_IMAGE_HEIGHT,
          // Neutral gray letterbox, not `background.default`: that token is the page's own
          // background, so a portrait shot narrower than the card used to blend straight into
          // the page around the card instead of reading as its own contained image area.
          // `grey.300` (MUI's default gray scale, not the GR `lightGrayBackground` token used
          // elsewhere for chart tracks) reads a touch darker against the white card.
          backgroundColor: 'grey.300',
          // Focus ring sits inside the image so it never escapes the surrounding
          // Card's `overflow: hidden` clip when the gallery is rendered edge-to-edge.
          '&:focus-visible': {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
        }}
      >
        {/*
        `width`/`height` are passed as props, not just in `style`: with `effect="blur"`,
        `LazyLoadImage` wraps the `<img>` in a `<span>` sized off those two props alone, so a
        `style`-only width is invisible to the wrapper. Left unset, the span shrinks to the
        image's own rendered size — for a landscape shot that is most of the box and the gap
        goes unnoticed, but a portrait shot then hugs the left edge instead of sitting in the
        center of the (much wider) box. The flex-centered parent above is the second half of
        the fix: it centers the shrunk-to-content span whenever a narrower image still ends up
        smaller than its box after this, e.g. a very tall, narrow capture.
      */}
        <LazyLoadImage
          alt={blueprintName}
          src={image.url}
          effect="blur"
          decoding="async"
          width="100%"
          height={GALLERY_IMAGE_HEIGHT}
          style={{ width: '100%', height: GALLERY_IMAGE_HEIGHT, objectFit: 'contain', display: 'block' }}
        />
      </Box>
      {/* Padded back in: the gallery itself is edge-to-edge, but text sitting flush
        against the card border reads as a clipping bug. `body2` + the game icon:
        this caption is the card's only attribution for the shot, so it gets more
        room here than the compact `caption` variant other carousels use. Left inset
        is a touch tighter than the right so the icon sits closer to the card edge.
        `stacked`: the card has a full row to spare, so the event name gets its own
        line instead of being truncated to whatever the game name left behind. */}
      <Box sx={{ pl: 1.5, pr: 2, pt: 1, pb: 1.5 }}>
        <LiveEventSourceCaption image={image} variant="body2" showGameIcon stacked onLockedClick={onLockedClick} />
      </Box>
    </>
  )
}

type BlueprintImageGalleryProps = {
  images: BlueprintFamilyExampleImage[]
  blueprintName: string
}

const BlueprintImageGallery: React.FC<BlueprintImageGalleryProps> = ({ images, blueprintName }) => {
  const { t } = useTranslation()
  // Position within `viewableImages`; `null` keeps the lightbox closed.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const userLanguage = useCurrentUserLanguage()
  const hasAccessToLiveEventsTracker = useLiveEventsTrackerAccessCheck()
  const { openLockedGameDialog, lockedGameDialog } = useLockedGameDialog()

  // The same rule as the caption under the shot (`LiveEventSourceCaption`): without the Live Events
  // Tracker role every game is outside the user's plan, free ones included, so the image and its
  // caption have to agree on which dialog a click opens.
  const isLockedForReader = useCallback(
    (image: BlueprintFamilyExampleImage) => !hasAccessToLiveEventsTracker || isLockedImage(image),
    [hasAccessToLiveEventsTracker]
  )

  // The lightbox is the deeper look a locked game doesn't get, so it only ever gets the open shots
  // — otherwise paging from an open one would land on a locked one.
  const viewableImages = useMemo(() => images.filter((image) => !isLockedForReader(image)), [images, isLockedForReader])

  const handleOpenModal = useCallback(
    (event: SyntheticEvent, index: number, lockedGame?: LockedGameSummary) => {
      const image = images[index]
      if (!image) return
      if (isLockedForReader(image)) {
        openLockedGameDialog(event, lockedGame ?? { name: resolveExampleImageGameName(image, userLanguage) || image.eventName || '' })
        return
      }
      setLightboxIndex(viewableImages.indexOf(image))
    },
    [images, viewableImages, isLockedForReader, openLockedGameDialog, userLanguage]
  )

  const handleCloseModal = useCallback(() => {
    setLightboxIndex(null)
  }, [])

  // The MarketLevelBlueprintsTabView only renders BlueprintImageGallery when there's at least one
  // image, so the single-image branch is the natural floor. We still guard for safety.
  if (images.length === 0) return null

  const renderModal = () => (
    <>
      {viewableImages.length > 0 && (
        <BlueprintScreenshotLightbox
          images={viewableImages}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={handleCloseModal}
          ariaLabel={blueprintName}
          getImageAlt={(image) => t('market-analytics:detail_panel_screenshot_reference_label', { n: images.indexOf(image) + 1 })}
        />
      )}
      {lockedGameDialog}
    </>
  )

  // Single-image entries skip the MultiSlider chrome entirely. With `infinite={true}` the slider
  // happily showed prev/next arrows that cycled back to the same image — i.e. looked broken.
  // A bare thumbnail with a click/keyboard handler is the honest affordance here.
  if (images.length === 1) {
    return (
      <>
        <GalleryImage
          image={images[0]}
          index={0}
          blueprintName={blueprintName}
          locked={isLockedForReader(images[0])}
          onOpen={handleOpenModal}
          onLockedClick={openLockedGameDialog}
        />
        {renderModal()}
      </>
    )
  }

  return (
    <>
      <MultiSlider
        infinite
        count={1}
        padding={0}
        showIndex
        floatingButtons
        floatingIndex
        mediaHeight={GALLERY_IMAGE_HEIGHT}
        ariaLabel={t('data-glossary:market_level_blueprints_carousel_label', { name: blueprintName })}
        ariaPrevLabel={t('data-glossary:market_level_blueprints_carousel_prev')}
        ariaNextLabel={t('data-glossary:market_level_blueprints_carousel_next')}
      >
        {images.map((image, index) => (
          <Box key={`${image.url}-${index}`}>
            <GalleryImage
              image={image}
              index={index}
              blueprintName={blueprintName}
              locked={isLockedForReader(image)}
              onOpen={handleOpenModal}
              onLockedClick={openLockedGameDialog}
            />
          </Box>
        ))}
      </MultiSlider>
      {renderModal()}
    </>
  )
}

// `minmax(0, 1fr)` columns rely on every descendant being able to shrink. `CardHeader` ships with
// `display: flex` and the title's inner `Typography` has `minWidth: auto` by default — both of
// which prevent the column from collapsing under a long title and cause horizontal page overflow.
// These overrides let the title wrap inside its column share.
const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
} as const

const cardHeaderSx = {
  // Tighter bottom padding (12px) than the default (16px) so the title reads as grouped with
  // the divider below it, matching the workspace `Cards with edge-to-edge GRTables` pattern.
  pb: 1.5,
  '& .MuiCardHeader-content': { minWidth: 0 },
  '& .MuiCardHeader-title': { overflowWrap: 'anywhere' },
} as const

// Reset `<p>` margins so the BE's `<p>...</p>` wrappers don't add unwanted whitespace, and force
// wrapping inside `<p>` so unusually long words (e.g. proper nouns, urls) can't push the grid
// column wider than its `minmax(0, 1fr)` share — which would cascade into a horizontally-scrolling
// page and an off-screen fixed tab bar.
const descriptionHtmlSx = {
  color: 'text.primary',
  typography: 'body2',
  overflowWrap: 'anywhere',
  '& p': { m: 0 },
  '& p + p': { mt: 1 },
} as const

// An event type on a card, with the color of the event category it belongs to.
type CardEventType = { typeId: string; colorHex: string }

type EventTypeChipsProps = {
  eventTypes: CardEventType[]
  getEventTypeName: (typeId: string) => string
}

// A family can run several event types, so every one is listed (leading type first, the order
// `familyGlossaryEventTypeMemberships` hands them over), the same placement the filter pills count.
const EventTypeChips: React.FC<EventTypeChipsProps> = ({ eventTypes, getEventTypeName }) => {
  const { t } = useTranslation()
  if (eventTypes.length === 0) return null

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }} data-testid="blueprint-family-event-types">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 'bold', lineHeight: '24px', flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {t('market-analytics:event_type')}
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ minWidth: 0 }}>
        {eventTypes.map(({ typeId, colorHex }) => (
          <Chip key={typeId} size="small" variant="outlined" label={getEventTypeName(typeId)} sx={categoryTintSx(colorHex)} />
        ))}
      </Stack>
    </Stack>
  )
}

const cardGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
  alignItems: 'stretch',
} as const

const SKELETON_CARD_COUNT = 9

const BlueprintCardSkeleton: React.FC = () => (
  <Card sx={cardSx}>
    <CardHeader title={<Skeleton variant="text" width="60%" />} sx={cardHeaderSx} />
    <CardContent sx={{ pt: 0 }}>
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="92%" />
      <Skeleton variant="text" width="78%" />
    </CardContent>
  </Card>
)

type BlueprintFamilyCardProps = {
  entry: LocalizedGlossaryEntry
  eventTypes: CardEventType[]
  hasLiveEventsTrackerAccess: boolean
  getEventTypeName: (typeId: string) => string
}

// Memoized so that typing in the search box or toggling a pill only mounts the cards that are new
// to the view: a card that stays on screen keeps its props (entry and type ids come from stable
// memoized maps), so it is not re-rendered along with its gallery, caption and carousel.
const BlueprintFamilyCard = memo(function BlueprintFamilyCard({ entry, eventTypes, hasLiveEventsTrackerAccess, getEventTypeName }: BlueprintFamilyCardProps) {
  const { t } = useTranslation()
  const hasImages = entry.images.length > 0

  return (
    <Card sx={cardSx}>
      <CardHeader title={entry.name || entry.fullName} titleTypographyProps={{ fontWeight: 'bold' }} sx={cardHeaderSx} />
      {/* Full-width divider seams the title off from whatever follows (an
          edge-to-edge image gallery, or the description directly). Matches the
          canonical "header + content" GR card pattern. */}
      <Divider />
      {/* The gallery is rendered as a Card sibling (not inside CardContent) so the
          image bleeds to the card's left and right borders, clipped by the Card's
          20px border-radius + overflow: hidden. The "1 / N" counter rendered by
          MultiSlider floats over the image's bottom-center, so the only chrome the
          gallery adds below the image is the source caption. */}
      {hasImages && <BlueprintImageGallery images={entry.images} blueprintName={entry.fullName} />}
      {/* Closes off the gallery block the same way the header's divider opens it,
          so the caption doesn't trail straight into the description (or into bare
          card padding, for entries with no description). */}
      {hasImages && <Divider />}
      <CardContent sx={{ pt: 2 }}>
        <EventTypeChips eventTypes={eventTypes} getEventTypeName={getEventTypeName} />
        {entry.descriptionHtml && <Box sx={descriptionHtmlSx} dangerouslySetInnerHTML={{ __html: entry.descriptionHtml }} />}
      </CardContent>
      {/* `mt: 'auto'` pins the footer to the card's bottom edge (the Card is a
          column flex box stretched to match its tallest grid sibling), rather
          than trailing right under a short description with empty space below. */}
      {hasLiveEventsTrackerAccess && (
        <CardActions sx={{ mt: 'auto', justifyContent: 'flex-end' }}>
          {/* Opens in a new tab: the glossary is a reference read alongside other
              work, so following a card out of it shouldn't lose the reader's place.
              Same treatment as every other Live Event Analytics deep link. */}
          <Button
            component={RouterLink}
            to={buildBlueprintFamilyPath(entry.id)}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<ArrowForward />}
            size="small"
          >
            {t('data-glossary:market_level_blueprints_view_in_analytics')}
          </Button>
        </CardActions>
      )}
    </Card>
  )
})

// Cards mounted on first paint and per scroll step. Each card carries a carousel, a caption with
// its own game lookup and a tooltip, so mounting the whole glossary at once is what made the tab
// slow to open; roughly four grid rows is enough to fill the first screen and the scroll margin.
const INITIAL_VISIBLE_CARDS = 12
const VISIBLE_CARDS_STEP = 12

const MarketLevelBlueprintsTabView: React.FC = () => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  // Gates the footer link to the Live Event Analytics blueprint detail page: without this
  // entitlement the destination is a locked page, the same reason `LiveEventCompactTrail` and
  // the event dialog's `EventHierarchyPath` hide their own blueprint family links.
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  // The glossary itself is open to every user, but the rest of `/blueprint-families/*` answers
  // 409 to anyone without one of these roles — the same set the backend checks.
  const hasInternalAccess = useInternalAccessCheck()
  const canLoadFamilyList = hasLiveEventsTrackerAccess || hasInternalAccess

  // Discontinued blueprint families are left out: they describe defunct event types and would
  // muddy a working reference. This used to be a reader-facing toggle, dropped 2026-09-10 when
  // we agreed discontinued stays out of the numbers everywhere, so the backend default decides.
  const { data: glossary, isLoading: glossaryLoading, isError: glossaryError } = useGetBlueprintFamilyGlossaryQuery()

  // The glossary payload is editorial only (names, descriptions, images); the event-type
  // membership every pill and heading below is built from lives on the catalogue's family list,
  // joined by family id. Called unscoped so it shares one RTK cache entry with the Live Event
  // Analytics catalogue and detail pages. The tag groups only serve that join, so they are
  // skipped along with it rather than flashing a filter-bar skeleton that never fills in.
  const { data: families, isLoading: familiesLoading } = useGetBlueprintFamiliesQuery({}, { skip: !canLoadFamilyList })
  const { data: tagGroups, isLoading: tagGroupsLoading } = useGetTagGroupsListQuery({ type: 'event', userLanguage }, { skip: !canLoadFamilyList })

  // URL-backed filter state, shared with the catalogue page. This tab only reads the `ecat` /
  // `etype` dimensions — there is no segment card here, so the game-taxonomy params a shared
  // catalogue link may carry are simply ignored. The hook writes nothing it isn't told to, so
  // no window / granularity / view params leak onto `/data-glossary/market-level-blueprints`.
  const state = useMarketCatalogueState()

  // The search stays in component state rather than the URL: it is a reading aid for this tab,
  // where the event params are part of the catalogue view a shared link carries.
  const [searchQuery, setSearchQuery] = useState('')
  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value), [])
  const handleClearSearch = useCallback(() => setSearchQuery(''), [])
  // The filtered-empty state offers one way out, so it has to undo both controls — clearing only
  // the pills would leave the reader staring at the same empty view.
  const handleClearFilters = useCallback(() => {
    setSearchQuery('')
    state.clearEvents()
  }, [state])

  // The input itself stays bound to `searchQuery` so keystrokes paint immediately; everything
  // derived from the query (filtering, pill recounts, the cards) follows the deferred value, which
  // React renders at lower priority and abandons when the next keystroke arrives.
  const deferredSearchQuery = useDeferredValue(searchQuery)
  const normalizedQuery = normalizeForSearch(deferredSearchQuery.trim())
  const searchActive = normalizedQuery.length > 0

  const allFamilies = useMemo(() => families ?? [], [families])

  // Event type → category, from the tag groups loaded above. The glossary files a family by the
  // categories of its curated (allowed) event types, which doesn't depend on the window or
  // platform the family list is requested for, so it needs this lookup rather than `eventTypes`.
  const categoryIdByTypeId = useMemo(() => buildEventTypeCategoryLookup(tagGroups), [tagGroups])

  const localizedEntries = useMemo(() => (glossary ?? []).map((entry) => getLocalizedEntry(entry, userLanguage)), [glossary, userLanguage])

  // The two controls are applied in sequence: the search narrows the universe of families, the
  // pills then partition what is left. Both the cards and the pill counts below read this set,
  // so the numbers on the pills always add up to the cards underneath them.
  const searchedEntries = useMemo(
    () => (searchActive ? localizedEntries.filter((entry) => matchesNameQuery(entry, normalizedQuery)) : localizedEntries),
    [localizedEntries, searchActive, normalizedQuery]
  )

  // Feeds the chip counts. Unsearched this is the whole family list — including families the
  // editorial glossary has not covered yet, which is how the pills have always counted.
  const searchedFamilies = useMemo(() => {
    if (!searchActive) return allFamilies
    const ids = new Set(searchedEntries.map((entry) => entry.id))
    return allFamilies.filter((family) => ids.has(family.id))
  }, [allFamilies, searchActive, searchedEntries])

  const tagGroupInfos = useMemo<TagGroupInfo[]>(
    () => (tagGroups ?? []).map((group) => ({ id: group.id, name: group.name, colorHex: group.colorHex, order: group.order })),
    [tagGroups]
  )
  const getEventTypeName = useCallback((typeId: string) => languageService.getTranslation('tags', typeId) || typeId, [])

  // Chip counts are DISTINCT FAMILIES per event category / event type, not the (family, event
  // type) placements the catalogue page ranks: a glossary card is rendered once under a category
  // heading even when the family runs two event types inside that category, so counting the
  // placements would print a pill number the cards below it can't add up to. Consequence worth
  // knowing: these pills can read slightly lower than the same pills on the catalogue page.
  // A family whose event types span two categories is still counted (and rendered) under each —
  // that is real membership, not duplication. Counted over the SEARCHED families, so the pills
  // partition what the search left rather than advertising cards the reader can no longer see.
  const eventCategoryItems = useMemo(() => buildPlacementItems(searchedFamilies, 'categoryId', categoryIdByTypeId), [searchedFamilies, categoryIdByTypeId])
  const eventTypeItems = useMemo(() => buildPlacementItems(searchedFamilies, 'typeId', categoryIdByTypeId), [searchedFamilies, categoryIdByTypeId])
  // Unsearched twins of the two lists above; only consulted to keep applied pills on screen.
  const allEventCategoryItems = useMemo(() => buildPlacementItems(allFamilies, 'categoryId', categoryIdByTypeId), [allFamilies, categoryIdByTypeId])
  const allEventTypeItems = useMemo(() => buildPlacementItems(allFamilies, 'typeId', categoryIdByTypeId), [allFamilies, categoryIdByTypeId])

  // Each family's placements, resolved once per family list instead of once per card per filter pass.
  const membershipsByFamilyId = useMemo(
    () => new Map(allFamilies.map((family) => [family.id, familyGlossaryEventTypeMemberships(family, categoryIdByTypeId)])),
    [allFamilies, categoryIdByTypeId]
  )
  // The event type pills' own category color per type, so a card chip is tinted exactly like its pill.
  const eventTypeColorById = useMemo(
    () => new Map(buildEventTypeOptions(allEventTypeItems, tagGroupInfos, [], getEventTypeName).map((option) => [option.id, option.colorHex])),
    [allEventTypeItems, tagGroupInfos, getEventTypeName]
  )
  const eventTypesByFamilyId = useMemo(
    () =>
      new Map(
        Array.from(membershipsByFamilyId, ([familyId, memberships]) => [
          familyId,
          Array.from(new Set(memberships.map((membership) => membership.typeId))).map((typeId) => ({
            typeId,
            colorHex: eventTypeColorById.get(typeId) ?? DEFAULT_TAG_GROUP_COLOR,
          })),
        ])
      ),
    [membershipsByFamilyId, eventTypeColorById]
  )

  // Cards that land in the trailing "Uncategorized" bucket: the family ran no catalogued event
  // type, or the family list doesn't know it. Counted over the cards themselves rather than the
  // family list, so the pill matches the heading it filters to.
  const uncategorizedEntryCount = useMemo(
    () => searchedEntries.filter((entry) => (membershipsByFamilyId.get(entry.id) ?? NO_MEMBERSHIPS).length === 0).length,
    [searchedEntries, membershipsByFamilyId]
  )

  const uncategorizedLabel = t('data-glossary:market_level_blueprints_uncategorized')

  // The "Uncategorized" pill trails the real categories, the same place its heading takes, and
  // only shows when there is something to filter to — so the pills add up to every card.
  const eventCategoryOptions = useMemo(() => {
    const options = retainAppliedOptions(
      buildEventCategoryOptions(allEventCategoryItems, tagGroupInfos),
      buildEventCategoryOptions(eventCategoryItems, tagGroupInfos),
      state.eventCategoryIds
    )
    if (uncategorizedEntryCount === 0 && !state.eventCategoryIds.includes(UNCATEGORIZED_GROUP_ID)) return options
    return [...options, { id: UNCATEGORIZED_GROUP_ID, name: uncategorizedLabel, colorHex: DEFAULT_TAG_GROUP_COLOR, blueprintCount: uncategorizedEntryCount }]
  }, [eventCategoryItems, allEventCategoryItems, tagGroupInfos, uncategorizedEntryCount, uncategorizedLabel, state.eventCategoryIds])
  // Event types are offered for the picked categories only. An applied type outlives its category
  // being deselected (the URL keeps it and the cards still filter on it), so it is kept in the row
  // — even with no category picked — for the reader to see and clear.
  const eventTypeOptions = useMemo(() => {
    const offeredTypeIds = new Set(
      state.eventCategoryIds.length > 0
        ? buildEventTypeOptions(allEventTypeItems, tagGroupInfos, state.eventCategoryIds, getEventTypeName).map((option) => option.id)
        : []
    )
    return retainAppliedOptions(
      buildEventTypeOptions(allEventTypeItems, tagGroupInfos, [], getEventTypeName),
      buildEventTypeOptions(eventTypeItems, tagGroupInfos, [], getEventTypeName),
      state.eventTypeIds,
      (option) => offeredTypeIds.has(option.id)
    )
  }, [eventTypeItems, allEventTypeItems, tagGroupInfos, state.eventCategoryIds, state.eventTypeIds, getEventTypeName])

  // Both the filter bar and the event-category grouping need the family list AND the event tag
  // groups: without the families there is nothing to filter on, and without the tag groups the
  // headings would be raw ids. Either request can fail, and neither is made for a reader without
  // Live Event Analytics access (which this tab does not require), so the tab falls back to its
  // previous shape (no bar, cards grouped by the category prefix in the BE's names) instead of blanking.
  const catalogueLoading = familiesLoading || tagGroupsLoading
  const catalogueDataReady = families !== undefined && tagGroups !== undefined

  // Only meaningful while the bar is on screen: in the degraded view the params may still be in
  // the URL (a shared link) but nothing is filtering on them, so a "clear your filters" prompt
  // would point at a control the reader cannot see. The search box lives inside that same bar,
  // so an active query can only ever mean the bar is on screen.
  const filtersActive = searchActive || (catalogueDataReady && (state.eventCategoryIds.length > 0 || state.eventTypeIds.length > 0))

  const deferredEventCategoryIds = useDeferredValue(state.eventCategoryIds)
  const deferredEventTypeIds = useDeferredValue(state.eventTypeIds)

  const groupedEntries = useMemo<CategoryGroup[]>(() => {
    if (!catalogueDataReady) return groupByNamePrefix(searchedEntries, uncategorizedLabel)

    const categorySet = deferredEventCategoryIds.length ? new Set(deferredEventCategoryIds) : null
    const typeSet = deferredEventTypeIds.length ? new Set(deferredEventTypeIds) : null
    // An uncategorized family runs no event type, so it can't answer an event type pick.
    const showUncategorized = !typeSet && (!categorySet || categorySet.has(UNCATEGORIZED_GROUP_ID))

    const byCategory = new Map<string, LocalizedGlossaryEntry[]>()
    const uncategorized: LocalizedGlossaryEntry[] = []

    for (const entry of searchedEntries) {
      const memberships = membershipsByFamilyId.get(entry.id) ?? NO_MEMBERSHIPS

      // The family ran no catalogued event type at all, so it lands in the trailing bucket
      // rather than being hidden — reachable unfiltered or through its own pill.
      if (memberships.length === 0) {
        if (showUncategorized) uncategorized.push(entry)
        continue
      }

      // Nothing matched just means "not this category / type".
      const matching = memberships.filter(
        (membership) => (!categorySet || categorySet.has(membership.categoryId)) && (!typeSet || typeSet.has(membership.typeId))
      )
      if (matching.length === 0) continue

      for (const categoryId of new Set(matching.map((membership) => membership.categoryId))) {
        const bucket = byCategory.get(categoryId)
        if (bucket) bucket.push(entry)
        else byCategory.set(categoryId, [entry])
      }
    }

    // Headings follow the tag group's own `order` (the taxonomy's reading order), not the
    // count-first order the pills above use — a glossary is browsed, not ranked. Categories the
    // tag-group list doesn't know keep their id as a heading and sort last.
    const nameById = new Map(tagGroupInfos.map((group) => [group.id, group.name]))
    const orderById = new Map(tagGroupInfos.map((group) => [group.id, group.order ?? Number.MAX_SAFE_INTEGER]))
    const groups = Array.from(byCategory.entries())
      .map(([id, items]) => ({ id, name: nameById.get(id) ?? id, items: items.sort(byLocalizedName) }))
      .sort((a, b) => {
        const orderA = orderById.get(a.id) ?? Number.MAX_SAFE_INTEGER
        const orderB = orderById.get(b.id) ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB || a.name.localeCompare(b.name)
      })

    if (uncategorized.length) {
      groups.push({ id: UNCATEGORIZED_GROUP_ID, name: uncategorizedLabel, items: uncategorized.sort(byLocalizedName) })
    }
    return groups
  }, [searchedEntries, uncategorizedLabel, catalogueDataReady, deferredEventCategoryIds, deferredEventTypeIds, membershipsByFamilyId, tagGroupInfos])

  // Only a prefix of the cards is mounted; the rest follow as the reader scrolls. The count starts
  // over whenever the search or a pill changes the result, so a filter pass never mounts more than
  // one screenful of fresh cards.
  const totalCardCount = useMemo(() => groupedEntries.reduce((sum, group) => sum + group.items.length, 0), [groupedEntries])
  // Keyed on what the reader chose, not on the data: a refetch or a late catalogue response changes
  // `total` (which the hook clamps to) without throwing a reader deep in the list back to the top.
  const renderResetKey = useMemo(
    () => JSON.stringify([normalizedQuery, deferredEventCategoryIds, deferredEventTypeIds, userLanguage]),
    [normalizedQuery, deferredEventCategoryIds, deferredEventTypeIds, userLanguage]
  )
  const { visibleCount, hasMore, sentinelRef } = useIncrementalRender({
    total: totalCardCount,
    resetKey: renderResetKey,
    initialCount: INITIAL_VISIBLE_CARDS,
    step: VISIBLE_CARDS_STEP,
  })
  const visibleGroups = useMemo<VisibleCategoryGroup[]>(() => {
    let remaining = visibleCount
    const result: VisibleCategoryGroup[] = []
    for (const group of groupedEntries) {
      if (remaining <= 0) break
      result.push({ ...group, items: group.items.slice(0, remaining), totalCount: group.items.length })
      remaining -= group.items.length
    }
    return result
  }, [groupedEntries, visibleCount])

  const showSkeletons = glossaryLoading || catalogueLoading
  const isEmpty = !showSkeletons && !glossaryError && groupedEntries.length === 0

  return (
    <Box className="MarketLevelBlueprintsTabView">
      <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
        {t('data-glossary:market_level_blueprints_intro')}
      </Typography>

      {/* The event half of the Live Event Analytics filter chrome. The segment (game category /
          genre / subgenre) card that sits above it there is deliberately left out: a glossary is
          a reference for every reader, not a view of one segment. Hidden entirely when the data
          behind it never arrived — an inert bar would read as broken filters rather than as a
          reduced view. The family-name search rides in the card's header slot, so it goes with
          it: in the degraded view the cards fall back to the name-prefix grouping unfiltered. */}
      {(catalogueLoading || catalogueDataReady) && (
        <EventFilterCard
          eventCategoryOptions={eventCategoryOptions}
          eventTypeOptions={eventTypeOptions}
          selectedEventCategoryIds={state.eventCategoryIds}
          selectedEventTypeIds={state.eventTypeIds}
          onToggleEventCategory={state.toggleEventCategory}
          onToggleEventType={state.toggleEventType}
          onClearEventCategories={state.clearEventCategories}
          onClearEventTypes={state.clearEventTypes}
          onClearEvents={state.clearEvents}
          isLoading={catalogueLoading}
          showTypeRowWhenTypeApplied
          footerSlot={
            <TextField
              size="small"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('data-glossary:market_level_blueprints_search_placeholder')}
              inputProps={{ 'aria-label': t('data-glossary:market_level_blueprints_search_aria') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end" aria-label={t('data-glossary:market_level_blueprints_search_clear')} onClick={handleClearSearch}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          }
        />
      )}

      {showSkeletons && (
        <Box sx={cardGridSx}>
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
            <BlueprintCardSkeleton key={index} />
          ))}
        </Box>
      )}

      {!showSkeletons && glossaryError && (
        <Typography variant="body2" color="warning.main" sx={{ textAlign: 'center', py: 6 }}>
          {t('data-glossary:market_level_blueprints_load_failed')}
        </Typography>
      )}

      {/* Two different nothings. Without filters the glossary itself is empty; with filters the
          reader narrowed it to nothing and needs a way back out, not a dead end. */}
      {isEmpty &&
        (filtersActive ? (
          <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {t('data-glossary:market_level_blueprints_filtered_empty')}
            </Typography>
            <Button variant="contained" color="primary" onClick={handleClearFilters}>
              {t('data-glossary:market_level_blueprints_clear_filters')}
            </Button>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            {t('data-glossary:market_level_blueprints_empty')}
          </Typography>
        ))}

      {!showSkeletons &&
        !glossaryError &&
        visibleGroups.map(({ id, name, items, totalCount }) => (
          // Each event category is its own labelled section, so a screen reader can jump
          // between categories instead of walking one long list of cards.
          <Box key={id} component="section" aria-labelledby={groupHeadingId(id)} sx={{ mb: 5, '&:last-of-type': { mb: 0 } }}>
            <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 1.5 }}>
              <Typography id={groupHeadingId(id)} variant="h6" component="h2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('data-glossary:market_level_blueprints_section_count', { count: totalCount })}
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2.5 }} />
            <Box sx={cardGridSx}>
              {items.map((entry) => (
                // Event types come from the catalogue family list, which only loads for Live
                // Events Tracker / internal readers; the card shows them to the former only,
                // like the Live Event Analytics link in its footer.
                <BlueprintFamilyCard
                  key={entry.id}
                  entry={entry}
                  eventTypes={hasLiveEventsTrackerAccess ? eventTypesByFamilyId.get(entry.id) ?? NO_EVENT_TYPES : NO_EVENT_TYPES}
                  hasLiveEventsTrackerAccess={hasLiveEventsTrackerAccess}
                  getEventTypeName={getEventTypeName}
                />
              ))}
            </Box>
          </Box>
        ))}

      {!showSkeletons && !glossaryError && hasMore && <Box ref={sentinelRef} aria-hidden data-testid="blueprint-families-load-more" sx={{ height: 1 }} />}
    </Box>
  )
}

export default MarketLevelBlueprintsTabView
